import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bike,
  CalendarClock,
  Check,
  CheckCircle2,
  ChefHat,
  Clock,
  HandCoins,
  Inbox,
  MapPin,
  Minus,
  Pencil,
  Phone,
  Plus,
  Printer,
  Search,
  Timer,
  Wallet,
  X,
} from 'lucide-react'
import apiClient from '@/api/client'
import type { ActiveOnlineOrder, OnlineOrder } from '@/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import {
  READY_STEP,
  clampReadyMinutes,
  clockTime,
  formatMinutes,
  incomingUrgency,
  kitchenUrgency,
  minutesBetween,
  promiseLabel,
  promiseState,
  readyInLabel,
  type Urgency,
} from '@/lib/onlineOrderTiming'
import { isDeliveryFailure } from '@/lib/onlineOrderCancel'
import { CancelOnlineOrderDialog } from '@/components/admin/online-orders/CancelOnlineOrderDialog'
import { RemoveAcceptedItemDialog } from './RemoveAcceptedItemDialog'

// Shared with AdminOnlineOrders' SSE handler so a status change streamed from
// the KDS (order_updated) refreshes the board columns instantly.
export const ACTIVE_ONLINE_ORDERS_QUERY_KEY = ['online-orders', 'active'] as const

const FULFILLMENT_LABEL: Record<string, string> = {
  pickup: 'Pickup',
  delivery: 'Delivery',
  dine_in: 'Dine-in',
}

type FulfillmentFilter = 'all' | 'delivery' | 'pickup' | 'dine_in'

// ── Board ────────────────────────────────────────────────────────────────────
// Tap-to-advance dispatch board. The kitchen (KDS) drives cards through
// In kitchen → Ready by doing its normal job; the board owns only the last
// mile: Ready → Out for delivery (delivery) and → Delivered/Picked up
// ('served'), then payment settlement. 'completed' is never set here — it is
// payment-driven (recording the final tender flips it server-side), which is
// what keeps Day Close honest about outstanding rider cash.
export function ActiveOrdersBoard({
  pending,
  pendingLoading,
  busyId,
  onAccept,
  onReject,
  onEdit,
  onPrintKot,
  formatCurrency,
  active,
}: {
  pending: OnlineOrder[]
  pendingLoading: boolean
  busyId: string | null
  /** F-ONL-N5: readyInMinutes is the promise the customer is shown. */
  onAccept: (o: OnlineOrder, readyInMinutes: number) => void
  onReject: (o: OnlineOrder) => void
  /** F-FE-N193: remove items after phoning the customer. */
  onEdit: (o: OnlineOrder) => void
  onPrintKot: (o: OnlineOrder) => void
  formatCurrency: (n: number) => string
  active: boolean
}) {
  const queryClient = useQueryClient()
  const [fulfillment, setFulfillment] = useState<FulfillmentFilter>('all')
  const [q, setQ] = useState('')
  const [settleTarget, setSettleTarget] = useState<ActiveOnlineOrder | null>(null)
  // F-ONL-N6: the order being cancelled / marked as a failed delivery.
  const [cancelTarget, setCancelTarget] = useState<ActiveOnlineOrder | null>(null)
  // F-ONL-N5: the "ready in" each Incoming card will promise, once nudged.
  // Untouched cards use the server's suggestion.
  const [readyIn, setReadyIn] = useState<Record<string, number>>({})
  const readyInFor = (o: OnlineOrder) => readyIn[o.id] ?? clampReadyMinutes(o.suggested_minutes ?? 20)
  const nudgeReadyIn = (o: OnlineOrder, delta: number) =>
    setReadyIn((cur) => ({ ...cur, [o.id]: clampReadyMinutes(readyInFor(o) + delta) }))
  // F-FE-N193: the accepted order an item is being removed from (a void).
  const [removeTarget, setRemoveTarget] = useState<ActiveOnlineOrder | null>(null)
  // Remove item is offered until money comes in; a paid order needs a refund.
  const removeButton = (o: ActiveOnlineOrder) =>
    o.order_id && o.paid_amount <= 0 && !o.is_settled ? (
      <Button variant="ghost" size="sm" onClick={() => setRemoveTarget(o)} className="mb-1.5 w-full text-muted-foreground">
        <Pencil className="mr-1 h-3.5 w-3.5" />
        Remove item
      </Button>
    ) : null

  const { data, isLoading } = useQuery({
    queryKey: ACTIVE_ONLINE_ORDERS_QUERY_KEY,
    queryFn: () => apiClient.getActiveOnlineOrders().then((r) => r.data ?? []),
    // Poll fallback mirrors the pending inbox: SSE is the fast path, this
    // keeps the board honest if the stream drops or the tab is backgrounded.
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: 'always',
    staleTime: 0,
    enabled: active,
  })

  // Tick every 15s so the elapsed-time and due chips age without a refetch —
  // Incoming turns amber at 2 minutes, so 30s was too coarse.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => setNow(Date.now()), 15_000)
    return () => window.clearInterval(id)
  }, [active])

  const advanceMutation = useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: 'out_for_delivery' | 'served' }) =>
      apiClient.updateOrderStatus(orderId, status),
    onSuccess: (_res, { status }) => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_ONLINE_ORDERS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      toastHelpers.success(status === 'out_for_delivery' ? 'Marked out for delivery' : 'Marked handed over')
    },
    onError: (err: unknown) => {
      const e = err as { message?: string }
      toastHelpers.error('Could not update order', e?.message || 'Please try again.')
    },
  })

  const refreshAfterCancel = () => {
    queryClient.invalidateQueries({ queryKey: ACTIVE_ONLINE_ORDERS_QUERY_KEY })
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    queryClient.invalidateQueries({ queryKey: ['online-orders', 'history'] })
  }

  // F-ONL-N6: on every accepted card — "Cancel", or "Delivery failed" once a
  // delivery has left the building.
  const cancelAction = (o: ActiveOnlineOrder) => (
    <button
      type="button"
      onClick={() => setCancelTarget(o)}
      className="rounded px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-danger-surface hover:text-danger-fg"
    >
      {isDeliveryFailure(o) ? 'Delivery failed…' : 'Cancel order…'}
    </button>
  )

  const matches = (name?: string | null, phone?: string | null, orderNumber?: string | null) => {
    const term = q.trim().toLowerCase()
    if (!term) return true
    return [name, phone, orderNumber].some((v) => v?.toLowerCase().includes(term))
  }

  const filteredPending = useMemo(
    () =>
      pending.filter(
        (o) =>
          (fulfillment === 'all' || o.fulfillment_type === fulfillment) &&
          matches(o.customer_name, o.customer_phone, o.order_number),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pending, fulfillment, q],
  )

  const lanes = useMemo(() => {
    const rows = (data ?? []).filter(
      (o) =>
        (fulfillment === 'all' || o.fulfillment_type === fulfillment) &&
        matches(o.customer_name, o.customer_phone, o.order_number),
    )
    return {
      inKitchen: rows.filter((o) => ['pending', 'confirmed', 'preparing'].includes(o.order_status)),
      ready: rows.filter((o) => o.order_status === 'ready'),
      outForDelivery: rows.filter((o) => o.order_status === 'out_for_delivery'),
      toSettle: rows.filter((o) => o.order_status === 'served'),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, fulfillment, q])

  const loading = pendingLoading || isLoading

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-2 lg:p-2.5">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone or order #…"
            className="h-9 pl-8"
          />
        </div>
        <div className="flex shrink-0 gap-1.5">
          {(
            [
              ['all', 'All'],
              ['delivery', 'Delivery'],
              ['pickup', 'Pickup'],
              ['dine_in', 'Dine-in'],
            ] as [FulfillmentFilter, string][]
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFulfillment(value)}
              className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                fulfillment === value
                  ? 'border-primary bg-primary/10 font-medium text-primary'
                  : 'border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Lanes. Tablet (< xl): fixed min column widths + horizontal snap scroll so
          ~3 lanes stay readable; desktop (xl+): all 5 share the width, no scroll. */}
      <div className="min-h-0 flex-1 snap-x snap-proximity overflow-x-auto">
        <div className="grid h-full min-w-max grid-flow-col auto-cols-[minmax(230px,1fr)] gap-2.5 md:auto-cols-[minmax(272px,1fr)] lg:gap-3 xl:min-w-0 xl:auto-cols-fr">
          <Lane
            title="Incoming"
            icon={<Inbox className="h-4 w-4" />}
            count={filteredPending.length}
            tone="incoming"
            loading={loading}
            empty="No new orders"
          >
            {filteredPending.map((o) => {
              const mins = readyInFor(o)
              return (
                <BoardCard
                  key={o.id}
                  order={o}
                  now={now}
                  timing={o.requested_time ? 'elapsed' : 'incoming'}
                  formatCurrency={formatCurrency}
                  onPrintKot={onPrintKot}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busyId === o.id}
                    onClick={() => onEdit(o)}
                    className="mb-1.5 w-full text-muted-foreground"
                  >
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    Edit items
                  </Button>
                  {/* F-ONL-N5: the time the customer will be promised. Starts
                      from what they were already shown; ±5 to adjust. An order
                      for later is promised for the customer's own time. */}
                  {o.requested_time ? (
                    <p className="mb-1.5 flex items-center justify-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-xs text-foreground">
                      <CalendarClock className="h-3.5 w-3.5 text-primary" />
                      Promised for <span className="font-semibold">{formatScheduled(o.requested_time)}</span>
                    </p>
                  ) : (
                    <div className="mb-1.5 flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1">
                      <span className="text-xs text-muted-foreground">{readyInLabel(o.fulfillment_type)}</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          aria-label="Five minutes less"
                          disabled={busyId === o.id}
                          onClick={() => nudgeReadyIn(o, -READY_STEP)}
                          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-14 text-center text-sm font-semibold tabular-nums text-foreground">{mins} min</span>
                        <button
                          type="button"
                          aria-label="Five minutes more"
                          disabled={busyId === o.id}
                          onClick={() => nudgeReadyIn(o, READY_STEP)}
                          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busyId === o.id}
                      onClick={() => onReject(o)}
                      className="text-red-600 hover:bg-danger-surface hover:text-danger-fg"
                    >
                      <X className="mr-1 h-3.5 w-3.5" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      disabled={busyId === o.id}
                      onClick={() => onAccept(o, mins)}
                      className="bg-green-600 text-white hover:bg-green-700"
                    >
                      <Check className="mr-1 h-3.5 w-3.5" />
                      Accept
                    </Button>
                  </div>
                </BoardCard>
              )
            })}
          </Lane>

          <Lane
            title="In kitchen"
            icon={<ChefHat className="h-4 w-4" />}
            count={lanes.inKitchen.length}
            tone="kitchen"
            loading={loading}
            empty="Nothing cooking"
          >
            {lanes.inKitchen.map((o) => (
              <BoardCard key={o.id} order={o} now={now} timing="promise" formatCurrency={formatCurrency} onPrintKot={onPrintKot} footerAction={cancelAction(o)}>
                {removeButton(o)}
                <p className="text-center text-xs text-muted-foreground">
                  Kitchen is on it — moves to Ready when bumped on the KDS.
                </p>
              </BoardCard>
            ))}
          </Lane>

          <Lane
            title="Ready"
            icon={<CheckCircle2 className="h-4 w-4" />}
            count={lanes.ready.length}
            tone="ready"
            loading={loading}
            empty="Nothing at the pass"
          >
            {lanes.ready.map((o) => (
              <BoardCard key={o.id} order={o} now={now} timing="promise" formatCurrency={formatCurrency} onPrintKot={onPrintKot} footerAction={cancelAction(o)}>
                {removeButton(o)}
                {o.fulfillment_type === 'delivery' ? (
                  <Button
                    size="sm"
                    className="w-full bg-sky-600 text-white hover:bg-sky-700"
                    disabled={advanceMutation.isPending || !o.order_id}
                    onClick={() => o.order_id && advanceMutation.mutate({ orderId: o.order_id, status: 'out_for_delivery' })}
                  >
                    <Bike className="mr-1.5 h-4 w-4" />
                    Rider out
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={advanceMutation.isPending || !o.order_id}
                    onClick={() => o.order_id && advanceMutation.mutate({ orderId: o.order_id, status: 'served' })}
                  >
                    <Check className="mr-1.5 h-4 w-4" />
                    {o.fulfillment_type === 'dine_in' ? 'Served' : 'Picked up'}
                  </Button>
                )}
              </BoardCard>
            ))}
          </Lane>

          <Lane
            title="Out for delivery"
            icon={<Bike className="h-4 w-4" />}
            count={lanes.outForDelivery.length}
            tone="delivery"
            loading={loading}
            empty="No riders out"
          >
            {lanes.outForDelivery.map((o) => (
              <BoardCard key={o.id} order={o} now={now} timing="promise" formatCurrency={formatCurrency} onPrintKot={onPrintKot} footerAction={cancelAction(o)}>
                {removeButton(o)}
                <Button
                  size="sm"
                  className="w-full"
                  disabled={advanceMutation.isPending || !o.order_id}
                  onClick={() => o.order_id && advanceMutation.mutate({ orderId: o.order_id, status: 'served' })}
                >
                  <Check className="mr-1.5 h-4 w-4" />
                  Delivered
                </Button>
              </BoardCard>
            ))}
          </Lane>

          <Lane
            title="To settle"
            icon={<Wallet className="h-4 w-4" />}
            count={lanes.toSettle.length}
            tone="settle"
            loading={loading}
            empty="No payments outstanding"
          >
            {lanes.toSettle.map((o) => (
              <BoardCard
                key={o.id}
                order={o}
                now={now}
                timing="elapsed"
                formatCurrency={formatCurrency}
                onPrintKot={onPrintKot}
                footerAction={o.paid_amount > 0.004 ? undefined : cancelAction(o)}
              >
                {removeButton(o)}
                {o.is_settled ? (
                  <Badge variant="secondary" className="w-full justify-center py-1">
                    Paid
                  </Badge>
                ) : (
                  <div className="space-y-1">
                    {o.paid_amount > 0 && (
                      <p className="text-center text-xs text-muted-foreground">
                        {formatCurrency(o.paid_amount)} paid · {formatCurrency(Math.max(0, o.order_total - o.paid_amount))} due
                      </p>
                    )}
                    <Button
                      size="sm"
                      className="w-full bg-violet-600 text-white hover:bg-violet-700"
                      onClick={() => setSettleTarget(o)}
                    >
                      <HandCoins className="mr-1.5 h-4 w-4" />
                      Record payment
                    </Button>
                  </div>
                )}
              </BoardCard>
            ))}
          </Lane>
        </div>
      </div>

      <RemoveAcceptedItemDialog
        order={removeTarget}
        onClose={() => setRemoveTarget(null)}
        formatCurrency={formatCurrency}
      />

      <SettleDialog
        order={settleTarget}
        formatCurrency={formatCurrency}
        onClose={() => setSettleTarget(null)}
      />

      <CancelOnlineOrderDialog
        order={cancelTarget}
        formatCurrency={formatCurrency}
        onClose={() => setCancelTarget(null)}
        onCancelled={refreshAfterCancel}
      />
    </div>
  )
}

// ── Lane ─────────────────────────────────────────────────────────────────────
// Full class strings (no template fragments) so Tailwind JIT picks them up.
// Lane surfaces are tinted per stage; cards stay white (bg-card) for contrast.
const LANE_TONES = {
  incoming: {
    text: 'text-red-600',
    surface: 'border-red-200/70 bg-red-50/70 dark:border-red-900/40 dark:bg-red-950/20',
    divider: 'border-red-200/60 dark:border-red-900/30',
  },
  kitchen: {
    text: 'text-amber-600',
    surface: 'border-amber-200/70 bg-amber-50/70 dark:border-amber-900/40 dark:bg-amber-950/20',
    divider: 'border-amber-200/60 dark:border-amber-900/30',
  },
  ready: {
    text: 'text-emerald-600',
    surface: 'border-emerald-200/70 bg-emerald-50/70 dark:border-emerald-900/40 dark:bg-emerald-950/20',
    divider: 'border-emerald-200/60 dark:border-emerald-900/30',
  },
  delivery: {
    text: 'text-sky-600',
    surface: 'border-sky-200/70 bg-sky-50/70 dark:border-sky-900/40 dark:bg-sky-950/20',
    divider: 'border-sky-200/60 dark:border-sky-900/30',
  },
  settle: {
    text: 'text-violet-600',
    surface: 'border-violet-200/70 bg-violet-50/70 dark:border-violet-900/40 dark:bg-violet-950/20',
    divider: 'border-violet-200/60 dark:border-violet-900/30',
  },
} as const

function Lane({
  title,
  icon,
  count,
  tone,
  loading,
  empty,
  children,
}: {
  title: string
  icon: React.ReactNode
  count: number
  tone: keyof typeof LANE_TONES
  loading: boolean
  empty: string
  children: React.ReactNode
}) {
  const t = LANE_TONES[tone]
  return (
    <div className={`flex min-h-0 snap-start flex-col rounded-xl border ${t.surface}`}>
      <div className={`flex items-center gap-2 border-b px-3 py-2.5 ${t.divider}`}>
        <span className={t.text}>{icon}</span>
        <span className="text-sm font-semibold text-foreground">{title}</span>
        <Badge variant="secondary" className="ml-auto tabular-nums">
          {count}
        </Badge>
      </div>
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-2.5">
        {count === 0 ? (
          <p className="py-8 text-center text-xs text-muted-foreground">{loading ? 'Loading…' : empty}</p>
        ) : (
          children
        )}
      </div>
    </div>
  )
}

// ── Card ─────────────────────────────────────────────────────────────────────
// Full class strings so Tailwind JIT picks them up.
const URGENCY_TEXT: Record<Urgency, string> = {
  calm: 'text-muted-foreground',
  warn: 'text-amber-600 font-medium',
  late: 'text-red-600 font-semibold',
}

// Compact board card. `order` is the staging row for Incoming and the active
// DTO (staging fields + live order fields) everywhere else. `timing` picks the
// clock on the card (F-ONL-N4 / F-ONL-N5):
//   incoming — time waiting for an answer, amber at 2 min and red at 5, plus
//              the auto-cancel countdown when the restaurant turned that on;
//   promise  — time left against the promise made on Accept ("12m left",
//              "8m late"), falling back to time since placed for orders
//              accepted before promises existed;
//   elapsed  — time since placed (To settle: the food has gone).
function BoardCard({
  order,
  now,
  timing,
  formatCurrency,
  onPrintKot,
  footerAction,
  children,
}: {
  order: OnlineOrder
  now: number
  timing: 'incoming' | 'promise' | 'elapsed'
  formatCurrency: (n: number) => string
  onPrintKot: (o: OnlineOrder) => void
  footerAction?: React.ReactNode
  children: React.ReactNode
}) {
  const placedMs = new Date(order.created_at).getTime()
  const elapsedMin = isNaN(placedMs) ? null : minutesBetween(placedMs, now)
  const promisedMs = order.promised_at ? new Date(order.promised_at).getTime() : NaN

  let clock: { text: string; urgency: Urgency; title?: string } | null = null
  if (timing === 'promise' && !isNaN(promisedMs)) {
    // Short on the card ("35m left", "6m late") so the order number keeps its
    // line; the promised clock time is a hover away.
    const s = promiseState(promisedMs, now)
    clock = {
      text: promiseLabel(s),
      urgency: s.urgency,
      title: `Promised to the customer for ${clockTime(order.promised_at)}`,
    }
  } else if (elapsedMin != null) {
    clock = {
      text: formatMinutes(elapsedMin),
      urgency: timing === 'incoming' ? incomingUrgency(elapsedMin) : kitchenUrgency(elapsedMin),
      title: timing === 'incoming' ? 'Waiting for an answer' : 'Since the order was placed',
    }
  }
  // An order left unanswered past the red line gets a ring, not just a red
  // number — it is the one thing on the board that is costing a customer now.
  const unansweredLate = timing === 'incoming' && clock?.urgency === 'late'

  const expiresMs = timing === 'incoming' && order.expires_at ? new Date(order.expires_at).getTime() : NaN
  const expiresIn = isNaN(expiresMs) ? null : Math.max(0, Math.ceil((expiresMs - now) / 60_000))

  const itemCount = order.cart.reduce((n, l) => n + l.quantity, 0)
  const shown = order.cart.slice(0, 3)
  const hiddenCount = order.cart.length - shown.length

  return (
    <div
      className={`rounded-lg border bg-card p-2.5 shadow-sm ${
        unansweredLate ? 'border-red-500 ring-2 ring-red-500/40' : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" className="text-[10px]">
            {FULFILLMENT_LABEL[order.fulfillment_type] ?? order.fulfillment_type}
          </Badge>
          {/*
            F-BE-N54: the table, and deliberately the loudest thing on the card
            after the money. For a QR order this is the ONLY way to tell whose
            food it is — a "Dine-in" badge with no table names nobody. Given the
            default variant rather than `secondary` so it reads at a glance from
            arm's length, which is how this board is actually used.
          */}
          {order.table_number && (
            <Badge className="text-[10px]">Table {order.table_number}</Badge>
          )}
          {order.order_number && <span className="text-xs font-medium text-muted-foreground">#{order.order_number}</span>}
          {order.requested_time && (
            <Badge className="bg-amber-500 text-[10px] text-white hover:bg-amber-500" title="Ordered for later — make it for this time">
              For {formatScheduled(order.requested_time)}
            </Badge>
          )}
          {(order.edit_count ?? 0) > 0 && (
            <Badge variant="outline" className="text-[10px]">
              Edited
            </Badge>
          )}
        </div>
        {clock && (
          <span
            title={clock.title}
            className={`flex shrink-0 items-center gap-1 text-xs tabular-nums ${URGENCY_TEXT[clock.urgency]} ${
              unansweredLate ? 'motion-safe:animate-pulse' : ''
            }`}
          >
            <Clock className="h-3 w-3" />
            {clock.text}
          </span>
        )}
      </div>

      <p className="mt-1 text-sm font-semibold text-foreground">{order.customer_name || 'Guest'}</p>
      {order.customer_phone && (
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <Phone className="h-3 w-3" />
          {order.customer_phone}
        </p>
      )}
      {order.fulfillment_type === 'delivery' && (order.delivery_zone_name || order.delivery_address) && (
        <p className="mt-0.5 flex items-start gap-1 text-xs text-muted-foreground">
          <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="line-clamp-2">
            {order.delivery_zone_name ? `${order.delivery_zone_name} · ` : ''}
            {order.delivery_area && order.delivery_area !== order.delivery_zone_name ? `${order.delivery_area} · ` : ''}
            {order.delivery_address}
            {order.delivery_lat != null && order.delivery_lng != null && (
              <>
                {' '}
                <a
                  href={`https://maps.google.com/?q=${order.delivery_lat},${order.delivery_lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  Map
                </a>
              </>
            )}
          </span>
        </p>
      )}
      {order.fulfillment_type === 'delivery' &&
        order.delivery_zone_name &&
        (order.delivery_lat == null || order.delivery_lng == null) && (
          <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-500">
            No map pin — customer picked the area; call to confirm the exact address
          </p>
      )}

      <ul className="mt-2 space-y-0.5 border-t border-border pt-2 text-xs">
        {shown.map((line, i) => (
          <li key={i} className="flex justify-between gap-2">
            <span className="min-w-0 text-foreground">
              <span className="block truncate">
                <span className="font-medium tabular-nums">{line.quantity}×</span> {line.name}
              </span>
              {(line.modifier_labels?.length ?? 0) > 0 && (
                <span className="block truncate pl-4 text-[11px] text-muted-foreground">{line.modifier_labels!.join(', ')}</span>
              )}
            </span>
          </li>
        ))}
        {hiddenCount > 0 && <li className="text-muted-foreground">+{hiddenCount} more…</li>}
      </ul>
      {order.notes && (
        <p className="mt-1.5 rounded bg-muted/50 px-1.5 py-0.5 text-[11px] text-muted-foreground">Note: {order.notes}</p>
      )}
      {expiresIn != null && (
        <p className={`mt-1.5 flex items-center gap-1 text-[11px] ${expiresIn <= 2 ? 'font-medium text-red-600' : 'text-muted-foreground'}`}>
          <Timer className="h-3 w-3" />
          {expiresIn === 0 ? 'Cancelling now — answer it' : `Auto-cancels in ${formatMinutes(expiresIn)} if unanswered`}
        </p>
      )}

      <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
        <button
          type="button"
          onClick={() => onPrintKot(order)}
          title="Print KOT (ONLINE ORDER slip)"
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Printer className="h-3.5 w-3.5" />
        </button>
        <span className="text-xs text-muted-foreground">{itemCount} item{itemCount === 1 ? '' : 's'}</span>
        <span className="text-sm font-semibold tabular-nums text-foreground">{formatCurrency(order.total_amount)}</span>
      </div>

      <div className="mt-2">{children}</div>
      {footerAction && <div className="mt-1 flex justify-center">{footerAction}</div>}
    </div>
  )
}

// ── Settle dialog ────────────────────────────────────────────────────────────
// Records the final tender for a delivered/handed-over order. Full payment
// flips the order to 'completed' server-side, which removes it from the board
// and puts the cash in Day Close's reconciliation — the board itself never
// sets 'completed'. Defaults to the intent the order was billed at so the
// payment doesn't re-price the order at a different tax rate (F-MM-N4).
function SettleDialog({
  order,
  formatCurrency,
  onClose,
}: {
  order: ActiveOnlineOrder | null
  formatCurrency: (n: number) => string
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  // F-MM-N6: online orders are billed at the customer's CHOSEN tender at
  // placement. That decision is locked — the operator only confirms the money
  // arrived, they don't re-pick a tender (which used to re-bill the order and
  // throw amount_exceeds_balance). So the tender here IS the billed intent.
  const billedIntent: 'cash' | 'card' | 'online' =
    order?.pay_intent === 'card' || order?.pay_intent === 'online' ? order.pay_intent : 'cash'

  const remaining = order ? Math.max(0, order.order_total - order.paid_amount) : 0

  const settleMutation = useMutation({
    mutationFn: () => apiClient.processPayment(order!.order_id!, { payment_method: billedIntent, amount: remaining }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_ONLINE_ORDERS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      toastHelpers.success('Payment recorded', `Order #${order?.order_number ?? ''} settled — it will complete automatically.`)
      onClose()
    },
    onError: (err: unknown) => {
      const e = err as { message?: string }
      toastHelpers.error('Could not record payment', e?.message || 'Please try again.')
    },
  })

  const tenderLabel: Record<'cash' | 'card' | 'online', string> = {
    cash: 'Cash — collected on delivery',
    card: 'Card — card machine at the door',
    online: 'Online — bank transfer / wallet',
  }

  return (
    <Dialog open={!!order} onOpenChange={(open) => !open && !settleMutation.isPending && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>
            {order?.customer_name || 'Guest'}
            {order?.order_number ? ` · Order #${order.order_number}` : ''} — collects the money the rider/counter
            brought back and completes the order.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border bg-muted/40 p-3 text-center">
          <p className="text-xs text-muted-foreground">Amount due</p>
          <p className="text-2xl font-bold tabular-nums text-foreground">{formatCurrency(remaining)}</p>
          {order && order.paid_amount > 0 && (
            <p className="mt-0.5 text-xs text-muted-foreground">{formatCurrency(order.paid_amount)} already recorded</p>
          )}
        </div>

        <div className="flex flex-col items-center gap-1">
          <span className="rounded-full border border-primary bg-primary/10 px-4 py-1.5 text-sm font-medium capitalize text-primary">
            {tenderLabel[billedIntent]}
          </span>
          <p className="text-center text-xs text-muted-foreground">
            Payment method was chosen at order placement and is locked to this order.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={settleMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => settleMutation.mutate()}
            disabled={settleMutation.isPending || !order?.order_id || remaining <= 0}
            className="bg-violet-600 text-white hover:bg-violet-700"
          >
            <HandCoins className="mr-1.5 h-4 w-4" />
            {settleMutation.isPending ? 'Recording…' : `Record ${formatCurrency(remaining)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// "Fri 1:30 PM" for an order placed for later (F-FE-N198), in the device's
// clock — the counter is in the restaurant.
function formatScheduled(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'later'
  return d.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit', hour12: true })
}
